import * as React from "react";
import { Box, Chip, Checkbox, InputBase, IconButton, MenuItem, Menu, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import { MARKET_INDENTED_LIST } from "../../../utilities/constants";

function SelectorChip({ label, options, selected, onChange, disabled, renderOption }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const valueLabel = selected.length > 0 ? selected.join(", ") : label;

    const toggle = (code) => {
        onChange(selected.includes(code) ? selected.filter((value) => value !== code) : [...selected, code]);
    };

    return (
        <>
            <Chip
                label={valueLabel}
                color={selected.length > 0 ? "primary" : "default"}
                variant={selected.length > 0 ? "filled" : "outlined"}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                disabled={disabled}
                sx={{ borderRadius: "16px" }}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {options.map((option) => (
                    <MenuItem key={option.code} onClick={() => toggle(option.code)}>
                        <Checkbox checked={selected.includes(option.code)} />
                        {renderOption ? renderOption(option) : option.description}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default function ChatComposer({
    value,
    onChange,
    onSend,
    onCancel,
    loading,
    canSend,
    commodities,
    markets,
    selectedCommodities,
    selectedMarkets,
    onCommodityChange,
    onMarketChange,
    selectionLocked,
    scopeMissing,
    isGapAnalysis,
    onGapToggle,
}) {
    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            if (canSend && !loading) onSend(event);
        }
    };

    return (
        <Box sx={{ width: "100%", maxWidth: 760, mx: "auto" }}>
            <Paper
                component="form"
                elevation={0}
                onSubmit={(event) => event.preventDefault()}
                sx={{
                    p: "10px 12px",
                    display: "flex",
                    alignItems: "flex-end",
                    borderRadius: "24px",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    "&:focus-within": { borderColor: "primary.main" },
                }}
            >
                <InputBase
                    sx={{ ml: 1, flex: 1, fontSize: "1rem", lineHeight: 1.5 }}
                    placeholder="Ask about a commodity or market..."
                    inputProps={{ "aria-label": "Ask about a commodity or market" }}
                    multiline
                    maxRows={6}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <IconButton
                    type="button"
                    aria-label={loading ? "cancel" : "send"}
                    title={loading ? "Stop generating" : "Send"}
                    onClick={loading ? onCancel : onSend}
                    disabled={loading ? false : !canSend}
                    sx={(theme) => ({
                        ml: 1,
                        backgroundColor: loading || canSend ? "primary.main" : "action.disabledBackground",
                        color: loading || canSend ? "primary.contrastText" : "action.disabled",
                        "&:hover": { backgroundColor: loading || canSend ? "primary.dark" : "action.disabledBackground" },
                        ...(loading && { boxShadow: `0 0 0 6px ${alpha(theme.palette.primary.main, 0.2)}` }),
                    })}
                >
                    {loading ? <StopIcon /> : <SendIcon />}
                </IconButton>
            </Paper>

            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 1.5 }}>
                <SelectorChip
                    label="Commodity"
                    options={commodities}
                    selected={selectedCommodities}
                    onChange={onCommodityChange}
                    disabled={selectionLocked}
                />
                <SelectorChip
                    label="Market"
                    options={markets}
                    selected={selectedMarkets}
                    onChange={onMarketChange}
                    disabled={selectionLocked}
                    renderOption={(option) => (
                        <>
                            {MARKET_INDENTED_LIST.includes(option.code) && <Box component="span" sx={{ width: 24 }} />}
                            {option.description}
                        </>
                    )}
                />
                <Chip
                    label="Gap and Analysis"
                    color={isGapAnalysis ? "primary" : "default"}
                    variant={isGapAnalysis ? "filled" : "outlined"}
                    onClick={() => onGapToggle(!isGapAnalysis)}
                    sx={{ borderRadius: "16px" }}
                />
            </Box>

            {scopeMissing && (
                <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }}>
                    Select at least one Commodity and one Market to ask a question.
                </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1.5 }}>
                ieQip AI can make mistakes. Verify important information.
            </Typography>
        </Box>
    );
}
